import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { KeyFacts, ProTip } from '../../components/LessonUI';

const basicStorageCode = `// Core Persistence: Storage Interface
// Storing transactional data
localStorage.setItem('ff_session_id', '98221-A');
localStorage.setItem('ff_theme_pref', 'onyx-dark');

// Retrieving data
const theme = localStorage.getItem('ff_theme_pref');
console.log("Active UI Theme:", theme);

// Removal and Cleanup
localStorage.removeItem('ff_session_id');
localStorage.clear(); // Nuclear option: Clears ALL keys for this origin`;

const jsonStorageCode = `// Structural Persistence: Objects & Arrays
const userSystemState = {
    uid: 'FF-99',
    roles: ['admin', 'editor'],
    lastSync: Date.now()
};

// CRITICAL: Storage is string-only. We must serialize.
localStorage.setItem('ff_user_state', JSON.stringify(userSystemState));

// Grabbing and reconstructing the object
const rawData = localStorage.getItem('ff_user_state');
const state = rawData ? JSON.parse(rawData) : null;

console.log("Current User Role:", state?.roles[0]);`;

const syncEventCode = `// Cross-Tab Synchronization: The 'storage' Event
// This event fires in every tab EXCEPT the one that made the change.
window.addEventListener('storage', (event) => {
    console.log(\`Refreshed Link Detected: \${event.key}\`);
    console.log("Syncing from:", event.oldValue, "to:", event.newValue);

    if (event.key === 'ff_theme_pref') {
        applyTheme(event.newValue); // Real-time UI sync across all open tabs
    }
});`;

const JSLocalStorage = () => {
    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Persistence & State</div>
                <h1>Web Storage: Client-Side Data Architecture</h1>
                <p className="hero-lead">
                    Master the browser's persistent state engines. Engineer seamless cross-tab synchronization,
                    manage storage quotas, and build offline-resilient user experiences using
                    localStorage and sessionStorage.
                </p>
            </div>

            <div className="content-section">
                <h2>The Persistence Layer: localStorage vs Session</h2>
                <p>
                    Modern web applications need to maintain state across page refreshes and browser restarts.
                    The **Web Storage API** provides two simple, key-value stores: `localStorage` (which
                    persists indefinitely) and `sessionStorage` (which lasts only as long as the tab is open).
                </p>
                <p>
                    Unlike cookies, Web Storage data is never sent to the server with HTTP requests,
                    making it a more efficient and secure choice for client-only metadata like UI
                    preferences or draft form content.
                </p>
                <CodeBlock code={basicStorageCode} language="javascript" />
            </div>

            <div className="content-section">
                <h2>The 5MB Limit: Quota Management</h2>
                <p>
                    Every origin is granted a specific storage quotausually around **5MB to 10MB**.
                    Exceeding this limit will trigger a `QuotaExceededError`. As an engineer, you must
                    treat storage as a finite resource.
                </p>
                <ProTip><p>
                        Browsers do not automatically "garbage collect" localStorage. If you store data
                        without a cleanup strategy, you will eventually hit the limit. Always implement
                        a Time-to-Live (TTL) mechanism or a "least-recently-used" (LRU) cache logic
                        for high-frequency storage operations.
                    </p></ProTip>
            </div>

            <div className="content-section">
                <h2>Serialization: Storing Complex Architecture</h2>
                <p>
                    The `Storage` interface is strictly string-based. To store arrays or objects, you must
                    serialize them into JSON strings. This translation step is a common source of
                    performance bottlenecks if handled inside tight loops or with massive datasets.
                </p>
                <CodeBlock code={jsonStorageCode} language="javascript" />
            </div>

            <div className="content-section">
                <h2>Cross-Tab Sync: The Synchronization Protocol</h2>
                <p>
                    One of the most powerful (and underutilized) features of Web Storage is the `storage` event.
                    When a value is modified in one tab, an event is broadcast to all **other** open tabs
                    of the same origin. This allows you to synchronize global states (like logging out
                    or changing a theme) without a complex WebSocket setup.
                </p>
                <CodeBlock code={syncEventCode} language="javascript" />
            </div>

            <div className="content-section">
                <h2>When to Level Up: LocalStorage vs IndexedDB</h2>
                <p>
                    While LocalStorage is excellent for simple preferences, it is **synchronous**.
                    This means reading a large string can block the main thread and freeze your UI.
                    For heavy datasets, complex queries, or binary blobs (images/videos), you should
                    migrate to **IndexedDB**, a powerful asynchronous transactional database.
                </p>
            </div>

            <KeyFacts><ul>
                    <li>**Lifecycle:** Use `sessionStorage` for temporary state to avoid clutter.</li>
                    <li>**Safety:** Never store sensitive data (JWTs, session secrets) in Web Storage.</li>
                    <li>**Performance:** Avoid calling `getItem` inside high-frequency scroll or animation loops.</li>
                    <li>**Resilience:** Always wrap `JSON.parse` in try/catch to handle corrupted data.</li>
                    <li>**Synchronization:** Use the `storage` event to keep multi-tab sessions in sync.</li>
                    <li>**Quotas:** Monitor usage and implement periodic cleanup of stale entries.</li>
                </ul></KeyFacts>

        </div>
    );
};

export default JSLocalStorage;


